/**
 * MiniCaseGraph
 * ─────────────
 * Static SVG rendering of a case-centric mini network.
 *
 * Why not react-force-graph here?
 *   At inline case-page sizes (≈240 px high) the force simulation collapses
 *   nodes into an overlapping "purple blob" before the user has a chance to
 *   interact. We don't need physics — we need a glanceable view of who this
 *   case is connected to.
 *
 * Layout
 *   • Center case at the middle of the canvas.
 *   • Connected cases arranged on a single ring around the center, evenly
 *     spaced. Labels orbit outside their node so they don't collide.
 *   • Edge color encodes confidence (matches the full-network legend).
 *
 * Interaction
 *   • Click any node → `onNodeClick({ caseId, ... })` (CaseNetworkSection
 *     uses this to navigate via `window.location`).
 *   • Hover any node → highlight + tooltip with the edge type label.
 */
import { useState, useMemo } from 'react';

const NODE_FILL = {
  case_missing_person: '#3B82F6',
  case_unidentified_remains: '#8B5CF6',
  person_poi: '#EF4444',
  vehicle: '#F59E0B',
  location: '#10B981',
  event: '#6366F1',
};

const CONF_STROKE = {
  confirmed: '#10B981',
  probable: '#F59E0B',
  possible: '#9CA3AF',
};

const EDGE_LABELS = {
  same_dump_site: 'Same Dump Site',
  same_location: 'Same Location',
  same_vehicle: 'Same Vehicle',
  same_suspect: 'Same Suspect',
  same_event: 'Same Event',
  missing_with: 'Missing With',
  family_relationship: 'Family',
  potential_match: 'Potential Match',
  reported_possible_link: 'Reported Link',
  investigative_lead: 'Investigative Lead',
  sighting_reported_at: 'Sighting',
  searched_at: 'Searched At',
  found_at: 'Found At',
  last_seen_at: 'Last Seen At',
};

const truncate = (s, n = 24) => {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
};

export const MiniCaseGraph = ({
  graphData,
  centerNodeId,
  height = 240,
  onNodeClick = () => {},
}) => {
  const [hovered, setHovered] = useState(null);

  const layout = useMemo(() => {
    if (!graphData?.nodes?.length) return null;

    const center = graphData.nodes.find((n) => n.id === centerNodeId)
      || graphData.nodes[0];
    const peers = graphData.nodes.filter((n) => n.id !== center.id);

    // SVG viewbox tuned for the inline mini-view aspect ratio
    const VB_W = 600;
    const VB_H = 280;
    const cx = VB_W / 2;
    const cy = VB_H / 2;
    const radius = Math.min(VB_W, VB_H) * 0.32;

    const peerPositions = peers.map((n, i) => {
      const angle = (i / peers.length) * Math.PI * 2 - Math.PI / 2;
      return { ...n, x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle), angle };
    });

    const nodeById = { [center.id]: { ...center, x: cx, y: cy } };
    peerPositions.forEach((p) => { nodeById[p.id] = p; });

    const edges = (graphData.edges || [])
      .map((e) => {
        const from = nodeById[e.from || e.source];
        const to = nodeById[e.to || e.target];
        if (!from || !to) return null;
        // Backend's /graph/case/{id}/network returns edges with `source`/`target`/`type`;
        // /graph/case/{id}/connections-style payloads use `from`/`to`/`edge_type`.
        // Accept both shapes so this component is consumer-agnostic.
        const edgeType = e.edge_type || e.type;
        return {
          id: e.id || `${from.id}_${to.id}_${edgeType}`,
          from, to,
          confidence: e.confidence || 'possible',
          edge_type: edgeType,
          label: e.label || e.relationship,
        };
      })
      .filter(Boolean);

    return { center: nodeById[center.id], peers: peerPositions, edges, VB_W, VB_H };
  }, [graphData, centerNodeId]);

  if (!layout) return null;
  const { center, peers, edges, VB_W, VB_H } = layout;

  return (
    <div className="relative w-full overflow-hidden rounded-lg border bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950"
         style={{ height }}>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Edges */}
        <g>
          {edges.map((e) => {
            const stroke = CONF_STROKE[e.confidence] || CONF_STROKE.possible;
            const isHovered = hovered && (hovered === e.from.id || hovered === e.to.id);
            return (
              <line
                key={e.id}
                x1={e.from.x} y1={e.from.y}
                x2={e.to.x}   y2={e.to.y}
                stroke={stroke}
                strokeWidth={isHovered ? 2.5 : 1.6}
                strokeOpacity={isHovered ? 1 : 0.55}
                strokeLinecap="round"
              />
            );
          })}
        </g>

        {/* Center node */}
        <g>
          <circle
            cx={center.x} cy={center.y} r={14}
            fill={NODE_FILL[center.type] || '#8B5CF6'}
            stroke="#fff" strokeWidth={2}
          />
          <text
            x={center.x} y={center.y + 30}
            textAnchor="middle"
            className="fill-slate-800 dark:fill-slate-100 font-semibold"
            style={{ fontSize: 11 }}
          >
            {truncate(center.label, 22)}
          </text>
        </g>

        {/* Peer nodes */}
        <g>
          {peers.map((n) => {
            const isHovered = hovered === n.id;
            const fill = NODE_FILL[n.type] || '#8B5CF6';
            // Label sits outside the ring radially
            const lx = center.x + (n.x - center.x) * 1.25;
            const ly = center.y + (n.y - center.y) * 1.25;
            const textAnchor =
              Math.abs(lx - center.x) < 8 ? 'middle' : (lx < center.x ? 'end' : 'start');
            return (
              <g key={n.id}
                 onMouseEnter={() => setHovered(n.id)}
                 onMouseLeave={() => setHovered(null)}
                 onClick={() => onNodeClick(n)}
                 style={{ cursor: n.caseId || n.case_id ? 'pointer' : 'default' }}>
                <circle
                  cx={n.x} cy={n.y} r={isHovered ? 11 : 9}
                  fill={fill}
                  stroke={isHovered ? '#0891b2' : '#fff'}
                  strokeWidth={isHovered ? 2.5 : 1.5}
                />
                <text
                  x={lx} y={ly + 4}
                  textAnchor={textAnchor}
                  className="fill-slate-700 dark:fill-slate-200"
                  style={{ fontSize: 10.5, pointerEvents: 'none' }}
                >
                  {truncate(n.label, 18)}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {/* Hover hint chip — shows the edge type to the hovered peer */}
      {hovered && (() => {
        const e = edges.find((x) => x.from.id === hovered || x.to.id === hovered);
        if (!e) return null;
        const lbl = EDGE_LABELS[e.edge_type] || e.edge_type;
        return (
          <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md text-[11px] bg-white/90 dark:bg-slate-800/90 border shadow-sm pointer-events-none"
               style={{ color: CONF_STROKE[e.confidence] || '#475569' }}>
            <span className="font-medium">{lbl}</span>
            <span className="ml-1.5 opacity-70 capitalize">· {e.confidence}</span>
          </div>
        );
      })()}

      {/* Mini legend */}
      <div className="absolute bottom-2 right-2 flex items-center gap-2 text-[10px] text-muted-foreground bg-white/80 dark:bg-slate-800/80 px-2 py-1 rounded-md backdrop-blur-sm">
        {Object.entries(CONF_STROKE).slice(0, 3).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1">
            <span className="inline-block w-3 h-0.5" style={{ background: v }} />
            <span className="capitalize">{k}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default MiniCaseGraph;
